import { RetailRateType } from '@ph-core/enums/retail-rate-type.enum';
import { ConfigService } from '@ph-core/services/config/config.service';
import { CustomerCostStrategy, DealerCostFormulaStrategy } from '@ph-env/model/environment.model';
import { SurchargeSelectionItem } from '@ph-model/filters/surcharge-selection-item';
import { Markup } from '@ph-model/login';
import { ProductItem } from '@ph-model/response';
import { productIdGenerator } from '@ph-shared/utils/product-id-generator';

import { SortOption, SortOptionIdEnum } from '../../model/sort-option';

const getEnvironmentConfig = () => ConfigService.getEnvironment();

export const resolveMarkupLabel = (productType: string): string => {
  const productTypeMarkups = getEnvironmentConfig().features.shopping.productTypeMarkups;
  const group = productTypeMarkups.find((entry) => entry.productTypes.includes(productType));
  return group?.label ?? productType;
};

const resolveProductMarkupType = (product: ProductItem): string =>
  product.markupProductType || product.productType;

const findProductTypeMarkupEntry = (
  productTypeMarkup: Markup['productTypeMarkup'] | undefined,
  productType: string
) => {
  const label = resolveMarkupLabel(productType);
  return productTypeMarkup?.find((entry) => resolveMarkupLabel(entry.productType) === label);
};
const DEFAULT_CUSTOMER_COST_STRATEGY: CustomerCostStrategy = {
  baseCost: 'dealerCost',
  capStrategy: 'none',
  applyMarkup: true,
};

const LEGACY_CUSTOMER_COST_STRATEGIES: Partial<Record<RetailRateType, CustomerCostStrategy>> = {
  FIXED: {
    baseCost: 'retailRate',
    capStrategy: 'none',
    applyMarkup: false,
  },
  MAX: {
    baseCost: 'retailRate',
    capStrategy: 'retailRate',
    applyMarkup: false,
  },
  'NON-REG-CAP': {
    baseCost: 'retailRate',
    capStrategy: 'maxCost',
    applyMarkup: false,
  },
  'NON-REG': {
    baseCost: 'dealerCost',
    capStrategy: 'none',
    applyMarkup: true,
  },
  NON_REG: {
    baseCost: 'dealerCost',
    capStrategy: 'none',
    applyMarkup: true,
  },
};

export const calculateCustomerCosts = (
  products: ProductItem[],
  markup: Markup,
  surchargesSelection: SurchargeSelectionItem[]
): ProductItem[] =>
  products.map((product: ProductItem) => recalculateCustomerCost(product, markup, surchargesSelection));

export const recalculateCustomerCost = (
  product: ProductItem,
  markup: Markup,
  surchargesSelection: SurchargeSelectionItem[]
): ProductItem =>
  clearStaleCustomerCostError(
    correctForRange(applyManualOverride(applySurcharges(applyMarkup(product, markup), surchargesSelection)))
  );

/**
 * Sets manualCustomerCostDelta from the typed cost relative to the pre-manual total
 * (base + markup + surcharges). Floor/cap are applied only to the displayed cost.
 */
export const overrideManualCustomerCost = (
  product: ProductItem,
  typedCustomerCost: number,
  markup: Markup,
  surchargesSelection: SurchargeSelectionItem[]
): ProductItem => {
  const preManualProduct = applySurcharges(applyMarkup(product, markup), surchargesSelection);
  const preManual = Number(preManualProduct.customerCost ?? 0);
  const typedCost = Number.isFinite(typedCustomerCost) ? Math.max(0, typedCustomerCost) : 0;

  return correctForRange(
    applyManualOverride({
      ...preManualProduct,
      manualCustomerCostDelta: typedCost - preManual,
    })
  );
};

const applyManualOverride = (product: ProductItem): ProductItem => ({
  ...product,
  customerCost: (product.manualCustomerCostDelta ?? 0) + (product.customerCost ?? 0),
});

const getCustomerCostStrategy = (product: ProductItem): CustomerCostStrategy => {
  const retailRateType = product.retailRateType as RetailRateType | undefined;
  if (!retailRateType) {
    return DEFAULT_CUSTOMER_COST_STRATEGY;
  }

  return (
    getEnvironmentConfig().features.shopping.customerCostStrategies?.[retailRateType] ??
    LEGACY_CUSTOMER_COST_STRATEGIES[retailRateType] ??
    DEFAULT_CUSTOMER_COST_STRATEGY
  );
};

/** Strategy-based cap used by recalculateCustomerCost and validation. */
export const getCustomerCostCap = (product: ProductItem): number | null => {
  const strategy = getCustomerCostStrategy(product);

  if (strategy.capStrategy === 'retailRate') {
    return Number(product.retailRate);
  }

  if (strategy.capStrategy === 'maxCost' && product.maxCost != null) {
    return Number(product.maxCost);
  }

  if (isDealerCostFormula(strategy.capStrategy)) {
    return resolveDealerFormulaValue(product, strategy.capStrategy);
  }

  return null;
};

export const isCustomerCostOverCap = (product: ProductItem, value: number): boolean => {
  if (product.retailRateType === 'FIXED' && value > +product.retailRate) {
    return true;
  }

  const cap = getCustomerCostCap(product);

  return cap != null && value > cap;
};

/** Display limit for cost-exceed errors (includes FIXED retail rate). */
export const getCustomerCostCapLimit = (product: ProductItem): number | null => {
  if (product.retailRateType === 'FIXED') {
    return +product.retailRate;
  }

  return getCustomerCostCap(product);
};

const isDealerCostFormula = (
  strategy: CustomerCostStrategy['baseCost'] | CustomerCostStrategy['capStrategy']
): strategy is DealerCostFormulaStrategy =>
  typeof strategy === 'object' && strategy !== null && strategy.source === 'dealerCost';

const resolveDealerFormulaValue = (product: ProductItem, formula: DealerCostFormulaStrategy): number =>
  Number(product.baseDealerCost ?? 0) * formula.multiplier + Number(formula.addend ?? 0);

const calculateBaseCustomerCost = (product: ProductItem, strategy: CustomerCostStrategy): number => {
  if (isDealerCostFormula(strategy.baseCost)) {
    return resolveDealerFormulaValue(product, strategy.baseCost);
  }

  switch (strategy.baseCost) {
    case 'retailRate':
      return Number(product.retailRate);
    case 'zero':
      return 0;
    default:
      return product.baseDealerCost!;
  }
};

const calculateMarkupCustomerCost = (product: ProductItem, markup: Markup): number => {
  const supportMarkupProductCodes = !!getEnvironmentConfig().features.shopping.productTypeMarkups.length;
  const markupType = resolveProductMarkupType(product);
  switch (markup.markupType) {
    case '$': {
      if (supportMarkupProductCodes) {
        const productMarkupDollar = findProductTypeMarkupEntry(
          markup.productTypeMarkup,
          markupType
        )?.markupDollar ?? markup.markupDollar;

        return product.baseDealerCost! + +productMarkupDollar;
      }
      return product.baseDealerCost! + +markup.markupDollar;
    }
    case 'x': {
      if (supportMarkupProductCodes) {
        const productMarkupMultiplier = findProductTypeMarkupEntry(
          markup.productTypeMarkup,
          markupType
        )?.markupMultiplier ?? markup.markupMultiplier;

        return product.baseDealerCost! * +productMarkupMultiplier;
      }
      return product.baseDealerCost! * +markup.markupMultiplier;
    }
    default:
      return product.baseDealerCost!;
  }
};

const applyMarkup = (product: ProductItem, markup: Markup): ProductItem => {
  const strategy = getCustomerCostStrategy(product);
  let customerCost = calculateBaseCustomerCost(product, strategy);

  if (getEnvironmentConfig().features.useMarkup && strategy.applyMarkup) {
    customerCost = calculateMarkupCustomerCost(product, markup);
  }

  return {
    ...product,
    customerCost,
    costPlaceholder: +product.retailRate,
  };
};

const applySurcharges = (product: ProductItem, surchargesSelection: SurchargeSelectionItem[]): ProductItem => {
  if (!surchargesSelection) {
    return product;
  }
  const productItem = {
    ...product,
    dealerCost: product.baseDealerCost ?? 0,
    retailRate: product.baseRetailRate ?? 0,
  };

  productItem.surchargeAdded = false;
  surchargesSelection.forEach((selection: SurchargeSelectionItem) => {
    const surcharges = productItem.surchargeOptions.surchargeOption;
    if (surcharges) {
      const matchingInProduct = surcharges.filter(
        ({ label }) =>
          (label.enUsLabel || label.enLabel || label.frCaLabel || label.frLabel)?.trim() === selection.name?.trim()
      );
      if (matchingInProduct.length === 1) {
        const optionInProduct = matchingInProduct[0];
        if (selection.selected) {
          if (
            product.retailRateType !== 'FIXED' ||
            getEnvironmentConfig().features.shopping.addSurchargeToCustomerCostForFixedRates
          ) {
            productItem.customerCost = Number(productItem.customerCost) + Number(optionInProduct.surchargeAmount);
          }
          productItem.dealerCost = Number(productItem.dealerCost) + Number(optionInProduct.surchargeAmount);
        }

        optionInProduct.added = selection.selected;
        if (selection.selected) {
          productItem.surchargeAdded = true;
        }
      }
    }
  });

  return productItem;
};

const clampDisplayedCustomerCost = (product: ProductItem, value: number): number => {
  if (value < 0) {
    return 0;
  }

  const cap = getCustomerCostCap(product);
  if (cap != null && value > cap) {
    return cap;
  }

  return value;
};

/** Clamp displayed customer cost only. Floor/cap residuals must not be written into the manual delta. */
const correctForRange = (product: ProductItem): ProductItem => {
  if (product.customerCost == null) {
    return product;
  }

  return {
    ...product,
    customerCost: clampDisplayedCustomerCost(product, product.customerCost),
  };
};

const NON_REG_CUSTOMER_COST_LIMIT = 50000;

/**
 * Surcharge and markup recalculation replaces the typed cost. Drop a cost-exceed
 * error when the new displayed cost is valid so Add to Cart is not left disabled.
 */
const clearStaleCustomerCostError = (product: ProductItem): ProductItem => {
  if (!product.errorFlag) {
    return product;
  }

  const value = Number(product.customerCost);
  if (isCustomerCostOverCap(product, value)) {
    return product;
  }

  if (product.retailRateType === 'NON-REG' && value > NON_REG_CUSTOMER_COST_LIMIT) {
    return product;
  }

  return {
    ...product,
    errorFlag: false,
    errorMessage: '',
  };
};

const extractMonthsFromStartingInterval = (startingInterval: string): number | null => {
  const match = startingInterval.match(/(\d+)\s*months?/i);

  return match ? Number(match[1]) : null;
};

const calculatePurchasedIntervalsCount = (product: ProductItem): number => {
  const { startingInterval, maxTerm } = product;
  if (!startingInterval || !maxTerm) {
    return 0;
  }
  const startingIntervalTerm = extractMonthsFromStartingInterval(startingInterval);
  if (startingIntervalTerm === null) {
    return 0;
  }
  const maxTermValue = parseInt(maxTerm, 10);
  if (isNaN(maxTermValue)) {
    return 0;
  }
  if (startingIntervalTerm > maxTermValue) {
    return 0;
  }

  return (maxTermValue - startingIntervalTerm) / 12 + 1;
};

export const productResponseDataMapper = (products: ProductItem[]): ProductItem[] =>
  products?.map((product: ProductItem) => ({
    ...product,
    customerTerm: product.customerTerm ? product.customerTerm : +product.maxTerm,
    baseDealerCost: product.baseDealerCost || +product.dealerCost,
    baseRetailRate: product.baseRetailRate || +product.retailRate,
    manualCustomerCostDelta: product.manualCustomerCostDelta || 0,
    errorFlag: false,
    errorMessage: '',
    purchasedIntervalsCount: calculatePurchasedIntervalsCount(product),
  }));

export const sortProductsFn = (products: ProductItem[], sortBy: SortOptionIdEnum) => {
  if (!products || !sortBy) {
    return products;
  }

  const selectedSortOption = SORT_OPTIONS.find(({ id }) => id === sortBy);
  const sortingPropertiesOrder = [...getEnvironmentConfig().productsSort.sortingPropertiesOrder];
  const modifiedSortingPropertiesOrder = sortingPropertiesOrder.sort((prop1, prop2) =>
    prop1 == selectedSortOption?.property ? -1 : prop2 == selectedSortOption?.property ? 1 : 0
  );

  return [...products].sort((a: ProductItem, b: ProductItem) => {
    let nameA: string | number;
    let nameB: string | number;
    for (const property of modifiedSortingPropertiesOrder) {
      switch (property) {
        // planSKU(unique value for every product) appending required to keep same order for products
        // with equal compatible values
        default:
        case 'product': {
          nameA = a.productDescription.enUsProductDescription.toLowerCase() + '     ' + productIdGenerator(a);
          nameB = b.productDescription.enUsProductDescription.toLowerCase() + '     ' + productIdGenerator(b);
          break;
        }
        case 'plan': {
          nameA = a.planName.enUsPlanName.toLowerCase() + '     ' + productIdGenerator(a);
          nameB = b.planName.enUsPlanName.toLowerCase() + '     ' + productIdGenerator(b);
          break;
        }
        case 'term': {
          nameA = +a.maxTerm;
          nameB = +b.maxTerm;
          break;
        }
        case 'cost': {
          nameA = +a.customerCost!;
          nameB = +b.customerCost!;
          break;
        }
      }

      if (nameA < nameB) {
        return selectedSortOption?.ascending ? -1 : 1; // sort string ascending
      } else if (nameA > nameB) {
        return selectedSortOption?.ascending ? 1 : -1;
      } else {
        // check on the next property
      }
    }

    return 0;
  });
};

export const SORT_OPTIONS: SortOption[] = [
  {
    id: SortOptionIdEnum.ProductAsc,
    property: 'product',
    ascending: true,
    label: 'shoppingPage.products.top-filter.product',
  },
  {
    id: SortOptionIdEnum.ProductDesc,
    property: 'product',
    ascending: false,
    label: 'shoppingPage.products.top-filter.product',
  },
  { id: SortOptionIdEnum.PlanAsc, property: 'plan', ascending: true, label: 'shoppingPage.products.top-filter.plan' },
  { id: SortOptionIdEnum.PlanDesc, property: 'plan', ascending: false, label: 'shoppingPage.products.top-filter.plan' },
  { id: SortOptionIdEnum.TermAsc, property: 'term', ascending: true, label: 'shoppingPage.products.top-filter.term' },
  { id: SortOptionIdEnum.TermDesc, property: 'term', ascending: false, label: 'shoppingPage.products.top-filter.term' },
  { id: SortOptionIdEnum.CostAsc, property: 'cost', ascending: true, label: 'shoppingPage.products.top-filter.cost' },
  { id: SortOptionIdEnum.CostDesc, property: 'cost', ascending: false, label: 'shoppingPage.products.top-filter.cost' },
];
